import { useState, useEffect, useMemo } from "react"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import { RadioGroup, RadioGroupItem } from "@/modules/shared/ui/components/radio-group"
import { usePluginInstalls } from "@/modules/marketplace/hooks/usePluginInstalls"
import { UserSummary, OrgRole } from "../../dtos/User"
import {
  NON_ASSIGNABLE_ROLES,
  TEAM_MEMBER_ROLE,
  getUserRoleIds,
  splitAssignableRoles,
  splitHeldRoles,
} from "../../utils/user-roles"
import { GetUserInfoByEmailAsync } from "@/modules/auth/infrastructure/auth-service"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { Alert, AlertDescription } from "@/modules/shared/ui/components/alert"
import { InfoIcon } from "lucide-react"

const ROLE_LABELS: Record<string, string> = {
  OrganizationOwner: "Organization Owner",
  TeamMember: "Team Member",
  RegisteredUser: "Registered User",
  SuperAdmin: "Super Admin",
  Admin: "Admin",
  Employee: "Employee",
  PayrollManager: "Payroll Manager",
  HrOperations: "HR Operations",
  LineManager: "Line Manager",
  HodFinanceDirector: "HOD / Finance Director",
  HrDirector: "HR Director",
  HrPayroll: "HR Payroll",
};

interface AddEditUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user: UserSummary | null
  isSubmitting: boolean
  roles: OrgRole[]
  onSubmit: (data: {
    email: string
    firstName: string
    lastName: string
    phoneNumber?: string
    roleIds: string[]
  }) => void
}

export function AddEditUserDialog({
  open,
  onOpenChange,
  user,
  isSubmitting,
  roles,
  onSubmit,
}: AddEditUserDialogProps) {
  const { activeFamilies } = usePluginInstalls()
  const [email, setEmail] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [baseRoleId, setBaseRoleId] = useState("")
  const [moduleRoleIds, setModuleRoleIds] = useState<string[]>([])
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [isExistingUser, setIsExistingUser] = useState(false)
  const [lockedFields, setLockedFields] = useState({
    firstName: false,
    lastName: false,
    phoneNumber: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEdit = !!user

  const { baseRoles, moduleGroups } = useMemo(
    () => splitAssignableRoles(roles, user ? getUserRoleIds(user) : [], activeFamilies),
    [roles, user, activeFamilies]
  )

  useEffect(() => {
    if (user) {
      setEmail(user.email)
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setPhoneNumber(user.phoneNumber || "")
      const held = splitHeldRoles(roles, getUserRoleIds(user))
      setBaseRoleId(held.baseRoleId)
      setModuleRoleIds(held.moduleRoleIds)
      setIsExistingUser(true)
      setLockedFields({
        firstName: !!user.firstName,
        lastName: !!user.lastName,
        phoneNumber: !!user.phoneNumber
      })
    } else {
      setEmail("")
      setFirstName("")
      setLastName("")
      setPhoneNumber("")
      const assignable = roles.filter(r => r.isBaseRole && !NON_ASSIGNABLE_ROLES.includes(r.name))
      const defaultRole = assignable.find(r => r.name === TEAM_MEMBER_ROLE) || assignable[0];
      setBaseRoleId(defaultRole ? defaultRole.id : "")
      setModuleRoleIds([])
      setIsExistingUser(false)
      setLockedFields({
        firstName: false,
        lastName: false,
        phoneNumber: false
      })
    }
  }, [user, open, roles])

  useEffect(() => {
    if (isEdit || !email || !email.includes("@") || !email.includes(".")) {
      if (!isEdit) {
        setIsExistingUser(false);
      }
      return;
    }

    const timer = setTimeout(async () => {
      setIsCheckingEmail(true);
      try {
        const userInfo = await GetUserInfoByEmailAsync(email);
        if (userInfo) {
          setFirstName(userInfo.firstName || "");
          setLastName(userInfo.lastName || "");
          setPhoneNumber(userInfo.phoneNumber || "");
          setIsExistingUser(true);
          setLockedFields({
            firstName: !!userInfo.firstName,
            lastName: !!userInfo.lastName,
            phoneNumber: !!userInfo.phoneNumber
          });
        } else {
          setIsExistingUser(false);
          setFirstName("");
          setLastName("");
          setPhoneNumber("");
          setLockedFields({
            firstName: false,
            lastName: false,
            phoneNumber: false
          });
        }
      } catch (error) {
        console.error("Failed to check user email", error);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [email, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!isEdit && !email) newErrors.email = "Email is required.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Invalid email address.";
    if (!baseRoleId) newErrors.roleIds = "Choose an access level.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSubmit({ email, firstName, lastName, phoneNumber, roleIds: [baseRoleId, ...moduleRoleIds] });
  }

  const selectBaseRole = (id: string) => {
    setBaseRoleId(id)
    setErrors((prev) => {
      const { roleIds: _roleIds, ...rest } = prev
      return rest
    })
  }

  const toggleModuleRole = (id: string, checked: boolean) => {
    setModuleRoleIds((prev) => (checked ? [...prev.filter((r) => r !== id), id] : prev.filter((r) => r !== id)))
  }

  const formatRoleName = (name: string) => {
    return ROLE_LABELS[name] || name.replace(/([A-Z])/g, " $1").trim();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-card max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? "Update the user's information and roles."
                : "Invite a new member to your organization."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (e.target.value) {
                    setErrors(prev => {
                      const { email: _email, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="email@example.com"
                disabled={isSubmitting || isEdit}
                className="disabled:bg-muted"
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
              {isEdit && (
                <p className="text-xs text-muted-foreground">Email cannot be changed after registration.</p>
              )}
            </div>

            {isCheckingEmail ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Spinner className="size-8 text-primary" />
                <p className="text-sm text-muted-foreground">Checking user information...</p>
              </div>
            ) : (
              <>
                {isExistingUser && !isEdit && (
                  <Alert className="bg-primary/5 border-primary/20">
                    <InfoIcon className="size-4 text-primary" />
                    <AlertDescription className="text-primary text-xs">
                      This user already has a platform account. Some details are locked.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    disabled={isSubmitting || lockedFields.firstName}
                    className={lockedFields.firstName ? "disabled:bg-muted" : ""}
                  />
                  {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    disabled={isSubmitting || lockedFields.lastName}
                    className={lockedFields.lastName ? "disabled:bg-muted" : ""}
                  />
                  {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    disabled={isSubmitting || lockedFields.phoneNumber}
                    className={lockedFields.phoneNumber ? "disabled:bg-muted" : ""}
                  />
                </div>
              </>
            )}
            {baseRoles.length > 0 && (
              <div className="grid gap-2">
                <Label id="access-level-label">Access level</Label>
                <RadioGroup
                  value={baseRoleId}
                  onValueChange={selectBaseRole}
                  aria-labelledby="access-level-label"
                  disabled={isSubmitting}
                  className="rounded-md border bg-card p-3"
                >
                  {baseRoles.map((role) => (
                    <div key={role.id} className="flex items-start gap-2">
                      <RadioGroupItem id={`role-${role.id}`} value={role.id} className="mt-0.5" />
                      <div className="grid gap-0.5">
                        <Label htmlFor={`role-${role.id}`} className="cursor-pointer font-normal">
                          {formatRoleName(role.name)}
                        </Label>
                        {role.description && (
                          <p className="text-xs text-muted-foreground">{role.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </RadioGroup>
                {errors.roleIds && <p className="text-sm text-destructive">{errors.roleIds}</p>}
              </div>
            )}
            {moduleGroups.map((group) => (
              <div
                key={group.moduleKey}
                className="grid gap-2"
                role="group"
                aria-labelledby={`module-${group.moduleKey}-label`}
              >
                <Label id={`module-${group.moduleKey}-label`}>{group.label}</Label>
                <p className="text-xs text-muted-foreground">
                  {group.installed
                    ? "Pick the roles this user needs here. Organization Owners have no access to this add-on until given a role."
                    : "This add-on is not installed, so roles can be removed but not added."}
                </p>
                <div className="grid gap-3 rounded-md border bg-card p-3">
                  {group.roles.map((role) => (
                    <div key={role.id} className="flex items-start gap-2">
                      <Checkbox
                        id={`role-${role.id}`}
                        className="mt-0.5"
                        checked={moduleRoleIds.includes(role.id)}
                        onCheckedChange={(checked) => toggleModuleRole(role.id, checked === true)}
                        disabled={isSubmitting || (!group.installed && !moduleRoleIds.includes(role.id))}
                      />
                      <div className="grid gap-0.5">
                        <Label htmlFor={`role-${role.id}`} className="cursor-pointer font-normal">
                          {formatRoleName(role.name)}
                        </Label>
                        {role.description && (
                          <p className="text-xs text-muted-foreground">{role.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Add User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
